// LeadToContactSync.js - Automatically sync validated leads to contact manager
import eventBus, { EventTypes } from './EventBus'
import toast from 'react-hot-toast'

class LeadToContactSync {
  constructor() {
    this.isInitialized = false
    this.syncHistory = []
    
    console.log('🔄 LeadToContactSync service initialized')
  }

  /**
   * Initialize auto-sync service
   */
  initialize() {
    if (this.isInitialized) return

    console.log('🚀 Initializing Lead-to-Contact auto-sync...')

    // Listen for lead validation events
    eventBus.on(EventTypes.LEAD_VALIDATED, async (data) => {
      await this.syncLeadToContacts(data)
    })

    // Also listen for new leads if auto-validation is enabled
    eventBus.on(EventTypes.LEAD_ADDED, async (data) => {
      const autoValidate = localStorage.getItem('auto_validate_leads') === 'true'
      if (autoValidate) {
        await this.validateAndSync(data)
      }
    })

    this.isInitialized = true
    console.log('✅ Lead-to-Contact auto-sync enabled')
  }

  /**
   * Validate a lead and sync if valid
   * @param {object} lead - Lead data
   * @param {object} FirebaseUserDataService - Firebase service (optional)
   * @param {string} tenantId - Tenant ID (optional)
   */
  async validateAndSync(lead, FirebaseUserDataService = null, tenantId = null) {
    console.log('🔍 Validating lead...', lead)

    try {
      // Basic email validation
      const isValidEmail = this.validateEmail(lead.email)
      
      if (!isValidEmail) {
        console.log('❌ Invalid email, moving to flagged leads')
        await this.moveToFlaggedLeads(lead, 'Invalid email format', FirebaseUserDataService, tenantId)
        return
      }

      // Check for duplicates in contacts
      const isDuplicate = await this.checkDuplicate(lead.email, FirebaseUserDataService, tenantId)
      
      if (isDuplicate) {
        console.log('⚠️ Duplicate contact found')
        await this.moveToFlaggedLeads(lead, 'Duplicate contact', FirebaseUserDataService, tenantId)
        return
      }

      // Update validation status in Firestore if available
      if (FirebaseUserDataService && tenantId && lead.id) {
        try {
          await FirebaseUserDataService.updateRecentLead(lead.id, {
            validationStatus: isValidEmail === 'risky' ? 'risky' : 'valid',
            validatedAt: new Date().toISOString()
          }, tenantId);
          console.log('✅ Validation status persisted to Firestore');
        } catch (error) {
          console.error('⚠️ Failed to persist validation status:', error);
        }
      }

      // If validation passes, emit validated event
      eventBus.emit(EventTypes.LEAD_VALIDATED, lead)

    } catch (error) {
      console.error('❌ Lead validation error:', error)
      await this.moveToFlaggedLeads(lead, `Validation error: ${error.message}`, FirebaseUserDataService, tenantId)
    }
  }

  /**
   * Sync validated lead to contacts
   * @param {object} lead - Validated lead data
   */
  async syncLeadToContacts(lead) {
    console.log('➡️ Syncing lead to contacts...', lead)

    try {
      // Get existing contacts
      const contacts = JSON.parse(localStorage.getItem('contacts') || '[]')

      // Transform lead to contact format
      const contact = {
        id: lead.id || `contact_${Date.now()}`,
        firstName: lead.firstName || lead.first_name || '',
        lastName: lead.lastName || lead.last_name || '',
        email: lead.email,
        companyName: lead.companyName || lead.company_name || lead.company || '',
        jobTitle: lead.jobTitle || lead.job_title || lead.title || '',
        phone: lead.phone || '',
        website: lead.website || '',
        industry: lead.industry || '',
        source: 'lead_generation',
        leadSource: lead.source || 'manual',
        status: 'new',
        tags: ['auto-synced', 'new-lead'],
        leadScore: 0,
        notes: `Auto-synced from lead generation on ${new Date().toLocaleDateString()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        customFields: lead.customFields || {}
      }

      // Add to contacts
      contacts.push(contact)
      localStorage.setItem('contacts', JSON.stringify(contacts))

      // Remove from leads staging
      const leads = JSON.parse(localStorage.getItem('leads_staging') || '[]')
      const updatedLeads = leads.filter(l => l.email !== lead.email)
      localStorage.setItem('leads_staging', JSON.stringify(updatedLeads))

      // Track sync
      this.syncHistory.push({
        leadEmail: lead.email,
        contactId: contact.id,
        syncedAt: new Date().toISOString(),
        status: 'success'
      })

      // Emit contact imported event
      eventBus.emit(EventTypes.CONTACT_IMPORTED, contact)

      console.log('✅ Lead synced to contacts successfully')
      toast.success(`Contact added: ${contact.email}`)

      return contact

    } catch (error) {
      console.error('❌ Sync error:', error)
      toast.error(`Failed to sync lead: ${error.message}`)
      
      this.syncHistory.push({
        leadEmail: lead.email,
        syncedAt: new Date().toISOString(),
        status: 'failed',
        error: error.message
      })
    }
  }

  /**
   * Move lead to flagged leads for review
   * @param {object} lead - Lead data
   * @param {string} reason - Reason for flagging
   * @param {object} FirebaseUserDataService - Firebase service (optional)
   * @param {string} tenantId - Tenant ID (optional)
   */
  async moveToFlaggedLeads(lead, reason, FirebaseUserDataService = null, tenantId = null) {
    console.log('🚩 Moving to flagged leads:', reason)

    // Update validation status in Firestore if available
    if (FirebaseUserDataService && tenantId && lead.id) {
      try {
        await FirebaseUserDataService.updateRecentLead(lead.id, {
          validationStatus: 'invalid',
          validatedAt: new Date().toISOString(),
          flagReason: reason,
          flaggedAt: new Date().toISOString()
        }, tenantId);
        console.log('✅ Lead flagged in Firestore');
      } catch (error) {
        console.error('⚠️ Failed to flag lead in Firestore:', error);
        // Fallback to localStorage
      }
    }

    // Fallback: Keep localStorage for backwards compatibility
    const flaggedLeads = JSON.parse(localStorage.getItem('flagged_leads') || '[]')
    
    flaggedLeads.push({
      ...lead,
      flaggedAt: new Date().toISOString(),
      flagReason: reason,
      status: 'flagged'
    })

    localStorage.setItem('flagged_leads', JSON.stringify(flaggedLeads))

    // Remove from staging
    const leads = JSON.parse(localStorage.getItem('leads_staging') || '[]')
    const updatedLeads = leads.filter(l => l.email !== lead.email)
    localStorage.setItem('leads_staging', JSON.stringify(updatedLeads))

    toast.error(`Lead flagged: ${reason}`)
  }

  /**
   * Validate email format (enhanced version)
   * @param {string} email - Email to validate
   * @returns {boolean|string} true if valid, 'risky' if risky, false if invalid
   */
  validateEmail(email) {
    if (!email || typeof email !== 'string') return false;
    
    // Basic format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    
    // Check for disposable email domains
    const domain = email.split('@')[1]?.toLowerCase();
    const disposableDomains = [
      '10minutemail.com', 'temp-mail.org', 'guerrillamail.com', 'mailinator.com',
      'throwaway.email', 'yopmail.com', 'tempail.com', 'maildrop.cc', 'tempmail.com',
      'sharklasers.com', 'guerrillamail.info', 'grr.la', 'guerrillamail.biz',
      'guerrillamail.de', 'spam4.me', 'mailcatch.com', 'getairmail.com'
    ];
    
    if (disposableDomains.includes(domain)) {
      console.log(`🚫 Disposable email detected: ${email}`);
      return false;
    }
    
    // Check for common spam patterns (mark as risky, not invalid)
    const riskyPatterns = [
      /noreply/i, /no-reply/i, /donotreply/i, /bounce/i,
      /test/i, /example/i, /sample/i
    ];
    
    if (riskyPatterns.some(pattern => email.match(pattern))) {
      console.log(`⚠️ Risky email pattern detected: ${email}`);
      return 'risky';
    }
    
    // Check for admin/automated addresses (risky)
    const riskyLocalParts = ['admin', 'info', 'support', 'sales', 'contact', 'hello', 'webmaster'];
    const localPart = email.split('@')[0]?.toLowerCase();
    if (riskyLocalParts.includes(localPart)) {
      console.log(`⚠️ Generic email address: ${email}`);
      return 'risky';
    }
    
    return true;
  }

  /**
   * Check if email already exists in contacts (using Firestore)
   * @param {string} email - Email to check
   * @param {object} FirebaseUserDataService - Firebase service
   * @param {string} tenantId - Tenant ID
   * @returns {boolean} Is duplicate
   */
  async checkDuplicate(email, FirebaseUserDataService, tenantId) {
    try {
      if (!FirebaseUserDataService || !tenantId) {
        // Fallback to localStorage if Firestore not available
        const contacts = JSON.parse(localStorage.getItem('contacts') || '[]');
        return contacts.some(c => c.email.toLowerCase() === email.toLowerCase());
      }
      
      const contacts = await FirebaseUserDataService.getContacts(tenantId);
      return contacts.some(c => c.email.toLowerCase() === email.toLowerCase());
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return false;
    }
  }

  /**
   * Bulk sync multiple leads
   * @param {array} leads - Array of leads to sync
   */
  async bulkSync(leads) {
    console.log(`📦 Bulk syncing ${leads.length} leads...`)

    let successCount = 0
    let failCount = 0

    for (const lead of leads) {
      try {
        await this.validateAndSync(lead)
        successCount++
      } catch (error) {
        console.error('Error syncing lead:', error)
        failCount++
      }
    }

    console.log(`✅ Bulk sync complete: ${successCount} succeeded, ${failCount} failed`)
    toast.success(`Synced ${successCount} leads to contacts`)

    if (failCount > 0) {
      toast.error(`${failCount} leads failed validation`)
    }
  }

  /**
   * Get sync history
   * @param {number} limit - Number of recent syncs
   * @returns {array} Sync history
   */
  getSyncHistory(limit = 50) {
    return this.syncHistory.slice(-limit)
  }

  /**
   * Get flagged leads
   * @returns {array} Flagged leads
   */
  getFlaggedLeads() {
    return JSON.parse(localStorage.getItem('flagged_leads') || '[]')
  }

  /**
   * Retry syncing a flagged lead
   * @param {string} leadEmail - Email of flagged lead
   */
  async retryFlaggedLead(leadEmail) {
    const flaggedLeads = this.getFlaggedLeads()
    const lead = flaggedLeads.find(l => l.email === leadEmail)

    if (!lead) {
      toast.error('Lead not found')
      return
    }

    await this.validateAndSync(lead)
  }

  /**
   * Enable/disable auto-validation
   * @param {boolean} enabled - Enable or disable
   */
  setAutoValidation(enabled) {
    localStorage.setItem('auto_validate_leads', enabled ? 'true' : 'false')
    console.log(`🔧 Auto-validation ${enabled ? 'enabled' : 'disabled'}`)
    toast.success(`Auto-validation ${enabled ? 'enabled' : 'disabled'}`)
  }
}

// Create singleton instance
const leadToContactSync = new LeadToContactSync()

export default leadToContactSync
